import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "./i18n/LanguageProvider";
import { LOCALE_CODES, DEFAULT_LOCALE } from "./i18n/config";
import { AuthProvider, useAuth } from "./context/AuthContext";
import SmoothScroll from "./components/SmoothScroll";
import CookieConsent from "./components/CookieConsent";
import Landing from "./pages/Landing";
import LegalPage from "./pages/LegalPage";
import Support from "./pages/Support";
import Contact from "./pages/Contact";
import DashboardLogin from "./pages/dashboard/DashboardLogin";
import Dashboard from "./pages/dashboard/Dashboard";

function detectLocale() {
  const nav = (navigator.language || "es").slice(0, 2).toLowerCase();
  return LOCALE_CODES.includes(nav) ? nav : DEFAULT_LOCALE;
}

// Validates :lang and provides i18n to marketing pages.
function LocaleLayout() {
  const { lang } = useParams();
  if (!LOCALE_CODES.includes(lang)) return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  return (
    <I18nProvider>
      <Outlet />
      <CookieConsent />
    </I18nProvider>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black text-white/40">Cargando…</div>
    );
  }
  if (!user) return <Navigate to="/dashboard/login" replace />;
  return children;
}

// Dashboard is served on dashboard.centauros.app in production; on the preview
// single host it lives under /dashboard. Both share this tree.
function DashboardRoutes() {
  return (
    <I18nProvider>
      <Routes>
        <Route path="login" element={<DashboardLogin />} />
        <Route index element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </I18nProvider>
  );
}

function isDashboardHost() {
  const h = window.location.hostname;
  return h.startsWith("dashboard.") || h.startsWith("admin.");
}

function App() {
  const dashHost = isDashboardHost();
  return (
    <AuthProvider>
      <BrowserRouter>
        <SmoothScroll />
        <Toaster position="top-center" richColors />
        <Routes>
          {dashHost && <Route path="/" element={<Navigate to="/dashboard" replace />} />}

          <Route path="/dashboard/*" element={<DashboardRoutes />} />

          <Route path="/:lang" element={<LocaleLayout />}>
            <Route index element={<Landing />} />
            <Route path="privacy" element={<LegalPage kind="privacy" />} />
            <Route path="terms" element={<LegalPage kind="terms" />} />
            <Route path="cookies" element={<LegalPage kind="cookies" />} />
            <Route path="support" element={<Support />} />
            <Route path="contact" element={<Contact />} />
          </Route>

          {/* Locale-less URLs (used by App Store / external links) redirect to the detected locale */}
          <Route path="/privacy" element={<Navigate to={`/${detectLocale()}/privacy`} replace />} />
          <Route path="/terms" element={<Navigate to={`/${detectLocale()}/terms`} replace />} />
          <Route path="/cookies" element={<Navigate to={`/${detectLocale()}/cookies`} replace />} />
          <Route path="/support" element={<Navigate to={`/${detectLocale()}/support`} replace />} />
          <Route path="/contact" element={<Navigate to={`/${detectLocale()}/contact`} replace />} />

          <Route path="/" element={<Navigate to={`/${detectLocale()}`} replace />} />
          <Route path="*" element={<Navigate to={`/${detectLocale()}`} replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
